import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SopAPI } from '../utils/api';
import { useAuth } from '../components/AuthProvider';
import { clearCopiedDoc } from '../utils/docClipboard';
import { isDocsMetaDoc } from '../utils/docOrganization';

/** Last successful list — so opening Full View does not refetch-as-empty. */
let docsCache = { workspaceId: null, allDocs: null };
const cacheListeners = new Set();

function cacheHit(workspaceId) {
  return docsCache.workspaceId === workspaceId && docsCache.allDocs !== null;
}

function writeDocsCache(workspaceId, list) {
  docsCache = { workspaceId, allDocs: list };
  cacheListeners.forEach((fn) => fn(workspaceId, list));
}

let listInflight = { workspaceId: null, promise: null };

function fetchDocsList(workspaceId) {
  if (listInflight.workspaceId === workspaceId && listInflight.promise) {
    return listInflight.promise;
  }
  const promise = SopAPI.list()
    .then((list) => {
      const next = list || [];
      writeDocsCache(workspaceId, next);
      return next;
    })
    .finally(() => {
      if (listInflight.promise === promise) {
        listInflight = { workspaceId: null, promise: null };
      }
    });
  listInflight = { workspaceId, promise };
  return promise;
}

/**
 * useDocs — load Doc metadata for the active workspace; reloads on workspace switch.
 *
 * The API returns every doc (active + binned). Consumers that need the library
 * or pickers should use `docs` (active only). Mission attachments and the bin
 * use `allDocs` / `trashedDocs` so binned docs stay addressable by id.
 */
export function useDocs({ eager = true } = {}) {
  const { workspaceId, loading: authLoading } = useAuth();
  const hit = cacheHit(workspaceId);
  const [allDocs, setAllDocsState] = useState(hit ? docsCache.allDocs : []);
  const [loading, setLoading] = useState(eager && !hit);
  const [error, setError] = useState('');
  const prevWorkspaceIdRef = useRef(undefined);

  const setAllDocs = useCallback((next) => {
    setAllDocsState((prev) => {
      const list = typeof next === 'function' ? next(prev) : next;
      if (workspaceId) writeDocsCache(workspaceId, list);
      return list;
    });
  }, [workspaceId]);

  const reload = useCallback(async ({ silent = false, force = false } = {}) => {
    setError('');
    const have = cacheHit(workspaceId);
    if (have && !force) {
      setAllDocsState(docsCache.allDocs);
      setLoading(false);
      return docsCache.allDocs;
    }
    if (!silent && !have) setLoading(true);
    try {
      const next = await fetchDocsList(workspaceId);
      setAllDocsState(next);
      return next;
    } catch (e) {
      setError(e.message || 'Failed to load Docs');
      if (!silent && !have) setAllDocsState([]);
      throw e;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    const onCache = (ws, list) => {
      if (ws !== workspaceId || !list) return;
      setAllDocsState(list);
      setLoading(false);
    };
    cacheListeners.add(onCache);
    return () => cacheListeners.delete(onCache);
  }, [workspaceId]);

  useEffect(() => {
    if (authLoading) return;

    const wsChanged =
      prevWorkspaceIdRef.current !== undefined &&
      prevWorkspaceIdRef.current !== workspaceId;
    prevWorkspaceIdRef.current = workspaceId;

    if (wsChanged) {
      writeDocsCache(null, null);
      setAllDocsState([]);
      setLoading(eager);
      clearCopiedDoc();
    }
    if (eager) reload();
    else if (cacheHit(workspaceId)) {
      setAllDocsState(docsCache.allDocs);
      setLoading(false);
    }
  }, [workspaceId, authLoading, reload, eager]);

  useEffect(() => {
    const onDocsChanged = (e) => {
      const { action, docId, deletedAt, doc } = e.detail || {};
      if (action === 'deleted' && docId) {
        setAllDocs((prev) =>
          prev.map((s) =>
            s.id === docId ? { ...s, deletedAt: deletedAt || new Date().toISOString() } : s
          )
        );
        return;
      }
      if (action === 'restored' && docId) {
        setAllDocs((prev) =>
          prev.map((s) =>
            s.id === docId
              ? { ...s, ...(doc || {}), deletedAt: null, expiresAt: null }
              : s
          )
        );
        return;
      }
      if (action === 'permanentlyDeleted' && docId) {
        setAllDocs((prev) => prev.filter((s) => s.id !== docId));
        return;
      }
      reload({ silent: true, force: true });
    };
    window.addEventListener('fokus:docs-changed', onDocsChanged);
    return () => window.removeEventListener('fokus:docs-changed', onDocsChanged);
  }, [reload]);

  // System records (doc organization) never surface as real docs anywhere
  const docs = useMemo(
    () => allDocs.filter((d) => !d.deletedAt && !isDocsMetaDoc(d)),
    [allDocs]
  );

  const trashedDocs = useMemo(
    () => allDocs.filter((d) => d.deletedAt && !isDocsMetaDoc(d)),
    [allDocs]
  );

  const createDoc = useCallback(
    async (data) => {
      setError('');
      const doc = await SopAPI.create(data);
      await reload({ force: true });
      return doc;
    },
    [reload]
  );

  const updateDoc = useCallback(
    async (docId, data) => {
      setError('');
      const doc = await SopAPI.update(docId, data);
      setAllDocs((prev) => prev.map((s) => (s.id === docId ? { ...s, ...doc } : s)));
      return doc;
    },
    [setAllDocs]
  );

  /** Move a doc to the bin (soft delete — recoverable for 30 days). */
  const deleteDoc = useCallback(async (docId) => {
    setError('');
    const deletedAt = new Date().toISOString();
    let snapshot = null;
    setAllDocs((prev) => {
      snapshot = prev.find((s) => s.id === docId) || null;
      return prev.map((s) => (s.id === docId ? { ...s, deletedAt } : s));
    });
    window.dispatchEvent(
      new CustomEvent('fokus:docs-changed', {
        detail: { action: 'deleted', docId, deletedAt },
      })
    );
    try {
      const result = await SopAPI.delete(docId);
      const trashed = result?.sop;
      if (trashed?.id) {
        setAllDocs((prev) =>
          prev.map((s) => (s.id === docId ? { ...s, ...trashed } : s))
        );
      }
    } catch (e) {
      setError(e.message || 'Failed to delete Doc');
      if (snapshot) {
        setAllDocs((prev) =>
          prev.map((s) => (s.id === docId ? snapshot : s))
        );
      }
      window.dispatchEvent(
        new CustomEvent('fokus:docs-changed', {
          detail: { action: 'restored', docId, doc: snapshot },
        })
      );
      throw e;
    }
  }, [setAllDocs]);

  /**
   * Bring a binned doc back. Mission links and per-mission checkbox state
   * were never removed, so the doc lights back up everywhere as it was.
   */
  const restoreDoc = useCallback(async (docId) => {
    setError('');
    const doc = await SopAPI.restore(docId);
    if (doc?.id) {
      setAllDocs((prev) =>
        prev.map((s) =>
          s.id === docId ? { ...s, ...doc, deletedAt: null, expiresAt: null } : s
        )
      );
    }
    window.dispatchEvent(
      new CustomEvent('fokus:docs-changed', {
        detail: { action: 'restored', docId, doc },
      })
    );
    return doc;
  }, [setAllDocs]);

  /** Point of no return — the doc record is gone; mission rows degrade to "Deleted doc". */
  const permanentlyDeleteDoc = useCallback(async (docId) => {
    setError('');
    await SopAPI.deletePermanent(docId);
    setAllDocs((prev) => prev.filter((s) => s.id !== docId));
    window.dispatchEvent(
      new CustomEvent('fokus:docs-changed', {
        detail: { action: 'permanentlyDeleted', docId },
      })
    );
  }, [setAllDocs]);

  return {
    docs,
    allDocs,
    trashedDocs,
    loading,
    error,
    setError,
    reload,
    createDoc,
    updateDoc,
    deleteDoc,
    restoreDoc,
    permanentlyDeleteDoc,
  };
}
